import { useState, useEffect } from "react";
import useStatusParam from "../../hooks/useStatusParam";
import apiClient from "../../api-services/ApiClient";
import AdminLayout from "../../app-components/AdminLayout";
import Alert from "../../app-components/alert/Alert";
import CurrencyToggle from "../../app-components/CurrencyToggle";
import ActiveFilters, {
  FilterChip,
} from "../../app-components/ui/ActiveFilters";
import ColumnMenu, {
  ColumnDef,
  useColumnVisibility,
} from "../../app-components/ui/ColumnMenu";
import DateFilter from "../../app-components/ui/DateFilter";
import EmptyState from "../../app-components/ui/EmptyState";
import PageHeader from "../../app-components/ui/PageHeader";
import Pagination from "../../app-components/ui/Pagination";
import StatusPill from "../../app-components/ui/StatusPill";
import { btnPrimary, card, inputBase } from "../../app-components/ui/styles";
import TableSkeleton from "../../app-components/ui/TableSkeleton";
import useCurrency from "../../hooks/useCurrency";
import { TransactionInterface } from "../../interface/transactions.interface";
import { ParamInterface } from "../../interface/users.interface";
import { formatDate, formatMoney } from "../../utils";

const TABS = [
  { value: "", label: "All" },
  { value: "PAID", label: "Paid" },
  { value: "PENDING", label: "Pending" },
  { value: "FAILED", label: "Failed" },
];

const PLAN_OPTIONS = [
  { value: "STANDARD", label: "Standard" },
  { value: "BRONZE", label: "Bronze" },
  { value: "SILVER", label: "Silver" },
  { value: "GOLD", label: "Gold" },
];

// Optional columns the admin can show or hide. Name is always shown.
const COLUMNS: ColumnDef[] = [
  { key: "sn", label: "S/N" },
  { key: "tranxId", label: "Tranx Id" },
  { key: "email", label: "Email", defaultVisible: false },
  { key: "amount", label: "Amount Paid" },
  { key: "plan", label: "Plan" },
  { key: "duration", label: "Duration" },
  { key: "planPrice", label: "Plan Price", defaultVisible: false },
  { key: "method", label: "Method" },
  { key: "reference", label: "Payment Reference" },
  { key: "status", label: "Status" },
  { key: "start", label: "Start" },
  { key: "end", label: "Ends" },
];

const th =
  "py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-hcMuted bg-hcHead sticky top-0 z-10 whitespace-nowrap";
const td = "py-3 px-4 text-left text-sm whitespace-nowrap";

const Transactions = () => {
  const [currency, setCurrency] = useCurrency();
  const [pageData, setPageData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(0);
  const [total, setTotal] = useState<number | undefined>(undefined);
  // status tab lives in the URL (?status=...) so sidebar submenus can link to it
  const [pageFilter, setPageFilter] = useStatusParam();
  // back to page 1 whenever the status changes (tab click or sidebar link)
  useEffect(() => setCurrentPage(1), [pageFilter]);

  const [searchInput, setSearchInput] = useState<string>("");
  const [searchInputDebounce, setSearchInputDebounce] = useState<string>("");
  const [dateRange, setDateRange] = useState<string>("");
  const [subscriptionPlan, setSubscriptionPlan] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [hasError, setHasError] = useState<boolean>(false);

  const { isVisible, toggle, visibleCount } = useColumnVisibility(
    "cols_transactions",
    COLUMNS
  );
  const show = (key: string) => isVisible(key);

  // effect for get transactions list
  useEffect(() => {
    const params = {
      page: currentPage,
      status: pageFilter,
      search: searchInput,
      dateRange: dateRange,
      subscriptionPlan: subscriptionPlan,
    };

    getTransactions(params);
  }, [currentPage, pageFilter, searchInput, dateRange, subscriptionPlan]);

  // debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchInput(searchInputDebounce);
      setCurrentPage(1);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInputDebounce]);

  useEffect(() => {
    setAlertMessage("");

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (end >= start) {
        setDateRange(startDate + "," + endDate);
        setCurrentPage(1);
      } else {
        setAlertMessage("Invalid date range.");
      }
    }
    if (!startDate && !endDate) {
      setDateRange("");
    }
  }, [startDate, endDate]);

  const getTransactions = async (params: ParamInterface) => {
    setLoading(true);
    setHasError(false);
    try {
      const res: any = await apiClient.get("transactions", { params });
      if (res && res.data) {
        setPageData(res.data.data);
        setCurrentPage(res.data.current_page);
        setLastPage(res.data.last_page);
        setTotal(res.data.total);
      } else {
        setPageData([]);
      }
    } catch (error) {
      console.error("Error fetching:", error);
      setAlertMessage("Unable to fetch records at the moment.");
      setHasError(true);
      setPageData([]);
    } finally {
      setLoading(false);
    }
  };

  const setDates = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  const clearFilters = () => {
    setSearchInputDebounce("");
    setSearchInput("");
    setSubscriptionPlan("");
    setDates("", "");
    setCurrentPage(1);
  };

  // Chips for every filter currently applied
  const chips: FilterChip[] = [];
  if (searchInput)
    chips.push({
      label: `Search: ${searchInput}`,
      onRemove: () => {
        setSearchInputDebounce("");
        setSearchInput("");
        setCurrentPage(1);
      },
    });
  if (subscriptionPlan)
    chips.push({
      label: `Plan: ${
        PLAN_OPTIONS.find((p) => p.value === subscriptionPlan)?.label ??
        subscriptionPlan
      }`,
      onRemove: () => {
        setSubscriptionPlan("");
        setCurrentPage(1);
      },
    });
  if (dateRange)
    chips.push({
      label: `Date: ${startDate} to ${endDate}`,
      onRemove: () => setDates("", ""),
    });

  const hasRecords = pageData?.length > 0;

  // Compute the starting serial number for the current page (page 1 -> 1, page 2 -> 11, ...)
  const serialStart = (currentPage - 1) * 10 + 1;

  return (
    <AdminLayout title="Admin | Subscriptions">
      <PageHeader
        title="Transactions"
        subtitle="Payments made for subscriptions"
        actions={<CurrencyToggle currency={currency} onChange={setCurrency} />}
      />

      <div className={`${card} py-5`}>
        {/* tabs */}
        <div className="px-5 flex items-center gap-1 mb-5 border-b border-hcBorder">
          {TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => {
                setPageFilter(t.value);
                setCurrentPage(1);
              }}
              className={`px-4 pb-3 text-sm font-medium -mb-px border-b-2 transition ${
                pageFilter === t.value
                  ? "text-honeConnectPurple border-honeConnectPurple"
                  : "text-honeConnectGray border-transparent hover:text-honeConnectBlack"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* filter bar */}
        <div className="px-5 flex items-center gap-3 flex-wrap mb-4">
          <div className="relative flex-1 min-w-[240px]">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg
                className="h-4 w-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                ></path>
              </svg>
            </span>
            <input
              value={searchInputDebounce}
              onChange={(e) => setSearchInputDebounce(e.target.value)}
              type="text"
              className={`${inputBase} pl-9 w-full`}
              placeholder="Search by tranx id, name, amount paid"
            />
          </div>

          <select
            aria-label="Plan"
            className={inputBase}
            value={subscriptionPlan}
            onChange={(e) => {
              setSubscriptionPlan(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All plans</option>
            {PLAN_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>

          <ColumnMenu columns={COLUMNS} isVisible={isVisible} onToggle={toggle} />
        </div>

        <div className="px-5 mb-4">
          <DateFilter
            startDate={startDate}
            endDate={endDate}
            onChange={setDates}
          />
        </div>

        <ActiveFilters chips={chips} onClearAll={clearFilters} />

        {/* record table */}
        {hasError && !loading ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center border-y border-hcBorder">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <svg
                className="w-7 h-7 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p className="font-semibold text-honeConnectBlack mb-1">
              Something went wrong
            </p>
            <p className="text-sm text-hcMuted mb-4">
              We couldn't load transactions. Please try again.
            </p>
            <button
              onClick={() =>
                getTransactions({
                  page: currentPage,
                  status: pageFilter,
                  search: searchInput,
                  dateRange: dateRange,
                  subscriptionPlan: subscriptionPlan,
                } as ParamInterface)
              }
              className={btnPrimary}
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="overflow-auto max-h-[68vh] border-y border-hcBorder">
            <table className="min-w-full bg-white">
              <thead>
                <tr>
                  {show("sn") && <th className={th}>S/N</th>}
                  {show("tranxId") && <th className={th}>Tranx Id</th>}
                  <th
                    className={`${th} left-0 z-20`}
                    style={{ position: "sticky" }}
                  >
                    Name
                  </th>
                  {show("email") && <th className={th}>Email</th>}
                  {show("amount") && <th className={th}>Amount Paid</th>}
                  {show("plan") && <th className={th}>Plan</th>}
                  {show("duration") && <th className={th}>Duration</th>}
                  {show("planPrice") && <th className={th}>Plan Price</th>}
                  {show("method") && <th className={th}>Method</th>}
                  {show("reference") && (
                    <th className={th}>Payment Reference</th>
                  )}
                  {show("status") && <th className={th}>Status</th>}
                  {show("start") && <th className={th}>Start</th>}
                  {show("end") && <th className={th}>Ends</th>}
                </tr>
              </thead>

              {loading ? (
                <TableSkeleton columns={visibleCount + 1} />
              ) : (
                <tbody className="text-honeConnectBlack">
                  {pageData?.map(
                    (transaction: TransactionInterface, index: number) => (
                      <tr
                        key={transaction.id}
                        className="group border-b border-hcBorder hover:bg-hcBg"
                      >
                        {show("sn") && (
                          <td className={`${td} text-hcMuted`}>
                            {serialStart + index}
                          </td>
                        )}

                        {show("tranxId") && (
                          <td className={td}>
                            {transaction?.transaction_code ?? "No Trans Id"}
                          </td>
                        )}

                        <td
                          className={`${td} font-medium bg-white group-hover:bg-hcBg z-[1]`}
                          style={{ position: "sticky", left: 0 }}
                        >
                          {transaction.subscription.user?.full_name ??
                            "No Name"}
                        </td>

                        {show("email") && (
                          <td className={td}>
                            {transaction.subscription.user?.email ?? "No Email"}
                          </td>
                        )}

                        {show("amount") && (
                          <td className={`${td} font-medium`}>
                            {formatMoney(
                              currency,
                              transaction?.amount,
                              transaction?.amount_usd
                            )}
                          </td>
                        )}

                        {show("plan") && (
                          <td className={td}>
                            {transaction.subscription.subscription_plan}
                          </td>
                        )}

                        {show("duration") && (
                          <td className={td}>
                            {
                              transaction.subscription
                                ?.subscription_plan_duration
                            }
                          </td>
                        )}

                        {show("planPrice") && (
                          <td className={td}>
                            {formatMoney(
                              currency,
                              transaction.subscription.plan?.price,
                              transaction.subscription.plan?.price_usd
                            )}
                          </td>
                        )}

                        {show("method") && (
                          <td className={td}>{transaction.payment_method}</td>
                        )}

                        {show("reference") && (
                          <td className={td}>{transaction.payment_reference}</td>
                        )}

                        {show("status") && (
                          <td className={td}>
                            {transaction.status && (
                              <StatusPill
                                label={transaction.status}
                                tone={
                                  transaction.status === "PENDING"
                                    ? "info"
                                    : undefined
                                }
                              />
                            )}
                          </td>
                        )}

                        {show("start") && (
                          <td className={td}>
                            {transaction.created_at
                              ? formatDate(
                                  transaction.subscription.start_date,
                                  "date"
                                )
                              : ""}
                          </td>
                        )}

                        {show("end") && (
                          <td className={td}>
                            {transaction.created_at
                              ? formatDate(
                                  transaction.subscription.end_date,
                                  "date"
                                )
                              : ""}
                          </td>
                        )}
                      </tr>
                    )
                  )}
                </tbody>
              )}
            </table>

            {!loading && !hasRecords && (
              <EmptyState
                title="No transactions found"
                message={
                  chips.length || pageFilter
                    ? "Try changing your search or filters."
                    : "There are no transactions to display yet."
                }
                onClear={chips.length ? clearFilters : undefined}
              />
            )}
          </div>
        )}

        {hasRecords && lastPage > 1 && (
          <Pagination
            currentPage={currentPage}
            lastPage={lastPage}
            total={total}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      <Alert message={alertMessage} />
    </AdminLayout>
  );
};

export default Transactions;
